import { useCallback, useEffect, useRef, useState } from 'react';
import { HottrMark } from '@/components/brand/HottrMark';
import {
  appendPinDigit,
  checkPin,
  keypadPinSpec,
  unlockPin,
} from './pinGate';
import { env } from '@/config/env';

const FAIL_CLEAR_MS = 800;

/**
 * Soft-launch PIN wall — same orb look as men/tv, world-style numeric keypad.
 * Public wordmark is HOTTR. only (no .app suffix).
 * Auto-submits when the expected number of digits is entered.
 *
 * Digit buffer lives on a ref; setState is a render-only write. Side effects
 * (error + auto-submit) stay out of the updater so React cannot replay a
 * check against a partial, flash invalid, and wipe the buffer between clicks.
 */
export function PinGateLanding({ onUnlocked }: { onUnlocked: () => void }) {
  const { numeric: isNumericPin, length: pinLength } = keypadPinSpec(
    env.pinAccessCode,
  );

  const [digits, setDigits] = useState<string[]>([]);
  const [fullCode, setFullCode] = useState('');
  const [error, setError] = useState(false);

  const digitsRef = useRef<string[]>([]);
  const onUnlockedRef = useRef(onUnlocked);
  onUnlockedRef.current = onUnlocked;
  const resetTimerRef = useRef<number | null>(null);
  const checkGenRef = useRef(0);

  const clearResetTimer = useCallback(() => {
    if (resetTimerRef.current != null) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  useEffect(() => () => clearResetTimer(), [clearResetTimer]);

  const unlockWith = useCallback(
    (pin: string, gen: number) => {
      if (gen !== checkGenRef.current) return false;
      if (checkPin(pin)) {
        unlockPin();
        onUnlockedRef.current();
        return true;
      }
      setError(true);
      clearResetTimer();
      resetTimerRef.current = window.setTimeout(() => {
        if (gen !== checkGenRef.current) return;
        digitsRef.current = [];
        setDigits([]);
        setFullCode('');
        setError(false);
        resetTimerRef.current = null;
      }, FAIL_CLEAR_MS);
      return false;
    },
    [clearResetTimer],
  );

  const handleDigit = useCallback(
    (d: string) => {
      const { digits: next, ready } = appendPinDigit(
        digitsRef.current,
        d,
        pinLength,
      );
      if (next.length === digitsRef.current.length) return;

      clearResetTimer();
      checkGenRef.current += 1;
      const gen = checkGenRef.current;
      digitsRef.current = next;
      setDigits(next);
      setError(false);

      if (isNumericPin && ready) {
        unlockWith(next.join(''), gen);
      }
    },
    [pinLength, isNumericPin, unlockWith, clearResetTimer],
  );

  const handleDelete = useCallback(() => {
    clearResetTimer();
    checkGenRef.current += 1;
    const next = digitsRef.current.slice(0, -1);
    digitsRef.current = next;
    setDigits(next);
    setError(false);
  }, [clearResetTimer]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA') return;
      if (e.key >= '0' && e.key <= '9') {
        e.preventDefault();
        handleDigit(e.key);
      } else if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        handleDelete();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleDigit, handleDelete]);

  return (
    <div className="relative flex min-h-dvh items-center justify-center overflow-hidden bg-ink text-white">
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="pin-orb pin-orb-a" />
        <div className="pin-orb pin-orb-b" />
        <div className="pin-orb pin-orb-c" />
        <div className="pin-orb pin-orb-d" />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(5,6,8,0.35) 0%, rgba(5,6,8,0.72) 50%, rgba(5,6,8,0.96) 100%)',
          }}
        />
      </div>

      <div className="relative z-10 w-full max-w-sm px-6">
        <div className="mb-8 flex flex-col items-center text-center">
          <HottrMark size="text-3xl md:text-4xl" surface=".app" />
          <p className="mt-6 font-display text-[10px] font-bold uppercase tracking-widest text-text-muted">
            — Private preview
          </p>
          <p className="mt-4 max-w-xs text-center font-display text-[14px] font-normal leading-relaxed text-text-secondary">
            Enter the access code to step into the room
            <span className="text-pink">.</span> Access lasts 24 hours, then you
            confirm again
            <span className="text-pink">.</span>
          </p>
        </div>

        <div className="border border-white/10 bg-charcoal/80 p-7 backdrop-blur-md">
          <div
            className="mb-8 flex justify-center gap-2"
            role="status"
            aria-label={`${digits.length} of ${pinLength} digits entered`}
          >
            {Array.from({ length: pinLength }).map((_, i) => (
              <div
                key={i}
                className={[
                  'flex h-12 w-9 items-center justify-center border text-lg font-display font-bold transition-all duration-200',
                  error
                    ? 'animate-[pin-shake_0.3s_ease-in-out] border-pink/70'
                    : digits[i]
                      ? 'border-pink/60 text-white shadow-glow-soft'
                      : 'border-white/15',
                ].join(' ')}
              >
                {digits[i] ? (
                  <span aria-hidden>•</span>
                ) : (
                  <span className="size-1.5 rounded-full bg-white/20" />
                )}
              </div>
            ))}
          </div>

          <div className="mx-auto grid max-w-[280px] grid-cols-3 gap-2.5">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'del'].map(
              (key) => {
                if (key === '') return <div key="blank" />;
                if (key === 'del') {
                  return (
                    <button
                      key="del"
                      type="button"
                      onClick={handleDelete}
                      className="flex h-14 items-center justify-center border border-white/12 bg-white/5 text-[11px] font-extrabold uppercase tracking-wider text-white/55 transition-all duration-base hover:border-white/25 hover:text-white active:scale-95"
                    >
                      Del
                    </button>
                  );
                }
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => handleDigit(key)}
                    className="flex h-14 items-center justify-center border border-white/12 bg-white/5 font-display text-xl font-bold text-white transition-all duration-base hover:border-pink/40 hover:bg-pink/10 active:scale-95 active:bg-pink/20"
                  >
                    {key}
                  </button>
                );
              },
            )}
          </div>

          {!isNumericPin && (
            <form
              className="mt-6 space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                checkGenRef.current += 1;
                unlockWith(fullCode, checkGenRef.current);
              }}
            >
              <label className="block text-center text-[10px] font-extrabold uppercase tracking-wider text-white/40">
                Or type full code
              </label>
              <input
                type="password"
                autoComplete="one-time-code"
                value={fullCode}
                onChange={(e) => {
                  setError(false);
                  setFullCode(e.target.value);
                }}
                className="w-full border border-white/12 bg-ink/80 px-4 py-3 text-center font-display text-base font-bold tracking-[0.15em] text-white outline-none focus:border-pink"
                placeholder="••••••••"
              />
              <button
                type="submit"
                disabled={fullCode.trim().length < 1}
                className="flex h-11 w-full items-center justify-center bg-pink font-display text-[11px] font-extrabold uppercase tracking-wider text-white shadow-glow-medium disabled:opacity-40"
              >
                Enter
              </button>
            </form>
          )}

          {error && (
            <p
              className="mt-6 text-center text-[11px] font-extrabold uppercase tracking-wider text-pink-300"
              role="alert"
            >
              Invalid code
            </p>
          )}
        </div>

        <p className="mt-8 text-center text-[9px] font-bold uppercase tracking-ultra text-white/32">
          Soft launch · 24h access · Melbourne
        </p>
      </div>

      <style>{`
        .pin-orb {
          position: absolute;
          border-radius: 9999px;
          filter: blur(48px);
          will-change: transform, opacity;
        }
        .pin-orb-a {
          width: min(70vw, 520px);
          height: min(70vw, 520px);
          left: -12%;
          top: -8%;
          background: radial-gradient(circle, rgba(255,12,182,0.55) 0%, rgba(255,12,182,0) 70%);
          animation: pin-drift-a 32s ease-in-out infinite;
        }
        .pin-orb-b {
          width: min(80vw, 640px);
          height: min(80vw, 640px);
          right: -18%;
          top: 18%;
          background: radial-gradient(circle, rgba(212,0,142,0.5) 0%, rgba(212,0,142,0) 68%);
          animation: pin-drift-b 40s ease-in-out infinite;
        }
        .pin-orb-c {
          width: min(75vw, 560px);
          height: min(75vw, 560px);
          left: 10%;
          bottom: -20%;
          background: radial-gradient(circle, rgba(125,6,89,0.65) 0%, rgba(125,6,89,0) 70%);
          animation: pin-drift-c 36s ease-in-out infinite;
        }
        .pin-orb-d {
          width: min(55vw, 400px);
          height: min(55vw, 400px);
          right: 18%;
          bottom: 8%;
          background: radial-gradient(circle, rgba(5,6,8,0.9) 0%, rgba(255,12,182,0.28) 40%, transparent 72%);
          animation: pin-drift-d 28s ease-in-out infinite;
        }
        @keyframes pin-drift-a {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.85; }
          50% { transform: translate(12%, 18%) scale(1.12); opacity: 1; }
        }
        @keyframes pin-drift-b {
          0%, 100% { transform: translate(0, 0) scale(1.05); opacity: 0.75; }
          50% { transform: translate(-16%, 10%) scale(0.95); opacity: 0.95; }
        }
        @keyframes pin-drift-c {
          0%, 100% { transform: translate(0, 0) scale(1); opacity: 0.9; }
          50% { transform: translate(14%, -12%) scale(1.08); opacity: 0.7; }
        }
        @keyframes pin-drift-d {
          0%, 100% { transform: translate(0, 0) scale(0.95); opacity: 0.7; }
          50% { transform: translate(-10%, -14%) scale(1.15); opacity: 0.95; }
        }
        @keyframes pin-shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-6px); }
          75% { transform: translateX(6px); }
        }
        @media (prefers-reduced-motion: reduce) {
          .pin-orb-a, .pin-orb-b, .pin-orb-c, .pin-orb-d {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
